import React, { useState, useEffect, useRef, useCallback } from 'react';
import { estimatesApi } from '../../services/estimatesApi';
import { Spinner } from './Spinner';

/**
 * Debounce hook
 */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * MasterFormat Autocomplete Component
 * Provides autocomplete for CSI MasterFormat codes and titles
 */
export function MasterFormatAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Search MasterFormat codes...',
  required = false,
  disabled = false,
  className = '',
  divisionFilter,  // Only show items from this division number
  levelFilter,     // Only show items at this level (1=division, 2=section, 3=subsection)
  autoFocus = false,  // Auto-focus input on mount
  showInitialList = false,  // Show results immediately on focus (browse mode)
  showDivisionHeaders = true,  // Show division group headers in results
  excludeCodes = [],  // MasterFormat codes to exclude from results
}) {
  const [inputValue, setInputValue] = useState(value?.title || '');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [error, setError] = useState(null);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const debouncedQuery = useDebounce(inputValue, 300);

  // Serialize excludeCodes for stable dependency comparison
  const excludeCodesKey = JSON.stringify(excludeCodes);

  // Search when debounced query changes
  useEffect(() => {
    const search = async () => {
      // In browse mode (showInitialList), allow empty query
      // But require divisionFilter or levelFilter to be set to avoid returning all items
      // Otherwise require at least 2 characters
      if (!showInitialList && (!debouncedQuery || debouncedQuery.length < 2)) {
        setResults([]);
        return;
      }

      // If browse mode but no filters are set, require typing to search
      // Note: use explicit undefined/null checks since divisionFilter could be 0
      if (showInitialList && !debouncedQuery && (divisionFilter === undefined || divisionFilter === null) && !levelFilter) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const parsedExcludeCodes = JSON.parse(excludeCodesKey);
        const response = await estimatesApi.searchMasterFormat(debouncedQuery || '', {
          limit: showInitialList ? 50 : 15,  // Show more results in browse mode
          division: divisionFilter,
          level: levelFilter,
          // Note: excludeCodes filtering is done client-side below
        });
        const data = response.data || response;
        let filteredResults = data.results || [];

        // Filter out excluded codes on the frontend
        if (parsedExcludeCodes.length > 0) {
          filteredResults = filteredResults.filter(item => !parsedExcludeCodes.includes(item.code));
        }

        setResults(filteredResults);
      } catch (err) {
        console.error('MasterFormat search error:', err);
        setError('Search failed');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [debouncedQuery, divisionFilter, levelFilter, showInitialList, excludeCodesKey]);

  // Auto-focus input on mount if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((item) => {
    const selected = {
      code: item.code,
      title: item.title,
      divisionNumber: item.divisionNumber,
      divisionTitle: item.divisionTitle,
    };

    setInputValue(item.title);
    setIsOpen(false);
    setHighlightedIndex(-1);

    if (onChange) {
      onChange(selected);
    }
    if (onSelect) {
      onSelect(selected);
    }
  }, [onChange, onSelect]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && results[highlightedIndex]) {
            handleSelect(results[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
        default:
          break;
      }
    },
    [isOpen, results, highlightedIndex, handleSelect]
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selection if user is typing new text
    if (onChange && value?.code) {
      onChange(null);
    }
  };

  const handleFocus = () => {
    // In browse mode, open dropdown immediately; otherwise require 2+ characters
    if (showInitialList || inputValue.length >= 2) {
      setIsOpen(true);
    }
  };

  // Group results by division
  const groupedResults = results.reduce((groups, item) => {
    const divisionKey = `${item.divisionNumber} - ${item.divisionTitle}`;
    if (!groups[divisionKey]) {
      groups[divisionKey] = [];
    }
    groups[divisionKey].push(item);
    return groups;
  }, {});

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500
            ${disabled ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-900'}
            ${value?.code ? 'border-green-500' : 'border-gray-300 dark:border-gray-700'}
            ${error ? 'border-red-500' : ''}
          `}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Spinner size="sm" />
          </div>
        )}
        {value?.code && !isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (showInitialList || inputValue.length >= 2 || results.length > 0) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto"
        >
          {error && (
            <div className="p-3 text-sm text-red-600 dark:text-red-400">{error}</div>
          )}

          {!isLoading && results.length === 0 && (showInitialList || inputValue.length >= 2) && !error && (
            <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
              No matches found. Try a different search term.
            </div>
          )}

          {!showInitialList && inputValue.length < 2 && (
            <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
              Type at least 2 characters to search...
            </div>
          )}

          {showDivisionHeaders ? (
            // Grouped by division with headers
            Object.entries(groupedResults).map(([division, items]) => (
              <div key={division}>
                <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-600 dark:text-gray-300 sticky top-0">
                  Division {division}
                </div>
                {items.map((item) => {
                  const globalIdx = results.indexOf(item);
                  return (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setHighlightedIndex(globalIdx)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer
                        ${highlightedIndex === globalIdx ? 'bg-blue-100 dark:bg-blue-900/40' : ''}
                      `}
                    >
                      <span className="text-gray-800 dark:text-gray-200">{item.title}</span>
                    </button>
                  );
                })}
              </div>
            ))
          ) : (
            // Flat list without headers
            results.map((item, idx) => (
              <button
                key={item.code}
                type="button"
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlightedIndex(idx)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer
                  ${highlightedIndex === idx ? 'bg-blue-100 dark:bg-blue-900/40' : ''}
                `}
              >
                <span className="text-gray-800 dark:text-gray-200">{item.title}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default MasterFormatAutocomplete;
